import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';

const PREVIEW_MAX = 5;

/**
 * @param {object|null} deleteItem
 *   Single: { id: string, title: string }
 *   Bulk: { items: Array<{ id: string, title: string }> }
 * @param {string} [resourceSingular='video']
 * @param {string} [resourcePlural='videos']
 */
export default function DeleteVideoDialog({
  deleteItem,
  deleting,
  onClose,
  onConfirm,
  resourceSingular = 'video',
  resourcePlural = 'videos',
}) {
  const isBulk = deleteItem?.items && Array.isArray(deleteItem.items);
  const bulkItems = isBulk ? deleteItem.items : [];
  const count = isBulk ? bulkItems.length : deleteItem ? 1 : 0;
  const open = Boolean(deleteItem) && count > 0;

  let dialogTitle = `Delete ${resourceSingular}?`;
  if (isBulk && count > 1) {
    dialogTitle = `Delete ${count} ${resourcePlural}?`;
  } else if (isBulk && count === 1) {
    dialogTitle = `Delete ${resourceSingular}?`;
  }

  const preview = isBulk ? bulkItems.slice(0, PREVIEW_MAX) : [];
  const rest = isBulk ? Math.max(0, bulkItems.length - PREVIEW_MAX) : 0;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        {deleteItem && !isBulk && (
          <Typography>
            This will permanently delete &quot;{deleteItem.title}&quot;.
          </Typography>
        )}
        {isBulk && (
          <Box>
            <Typography sx={{ mb: preview.length ? 1 : 0 }}>
              This will permanently delete{' '}
              <strong>{count}</strong> {count === 1 ? resourceSingular : resourcePlural}.
            </Typography>
            {preview.length > 0 && (
              <Box component='ul' sx={{ m: 0, pl: 2.5, my: 0 }}>
                {preview.map((it) => (
                  <li key={it.id}>
                    <Typography component='span' variant='body2'>
                      {it.title || '—'}
                    </Typography>
                  </li>
                ))}
              </Box>
            )}
            {rest > 0 && (
              <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                and {rest} more…
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>
          Cancel
        </Button>
        <Button
          color='error'
          variant='contained'
          onClick={onConfirm}
          disabled={deleting}
        >
          {deleting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
